import { DEFAULT_LOCALE, type SupportedLocale } from "./config";

export interface FormTranslationRow {
    form_name?: string | null;
    form_description?: string | null;
    form_instructions?: string | null;
    form_caption?: string | null;
}

export interface PageTranslationRow {
    page_id: string;
    page_label?: string | null;
    page_description?: string | null;
}

export interface InputTranslationRow {
    input_id: string;
    input_label?: string | null;
    input_caption?: string | null;
    input_description?: string | null;
    scale_options?:
        | {
              label: string;
              value: string | number;
              description?: string | null;
              segments?: { label: string; description?: string | null }[] | null;
          }[]
        | null;
    options?: string[] | null;
}

export interface LocalizableFormView {
    form_name?: string | null;
    form_description?: string | null;
    form_instructions?: string | null;
    form_caption?: string | null;
    pages?: Array<{
        page_id?: string;
        page_label?: string | null;
        page_description?: string | null;
        inputs?: Array<{
            input_id?: string;
            input_label?: string | null;
            input_caption?: string | null;
            input_description?: string | null;
            scale_options?:
                | {
                      label: string;
                      value: string | number;
                      description?: string | null;
                      segments?: { label: string; description?: string | null }[] | null;
                  }[]
                | null;
            options?: string[] | null;
        }> | null;
    }> | null;
}

export interface FormTranslationBundle {
    form?: FormTranslationRow | null;
    pages?: PageTranslationRow[];
    inputs?: InputTranslationRow[];
}

/** Overlay locale-specific copy onto a canonical form view. Structural IDs are unchanged. */
export function applyFormTranslations<T extends LocalizableFormView>(
    form: T,
    locale: SupportedLocale,
    translations: FormTranslationBundle
): T {
    if (locale === DEFAULT_LOCALE) {
        return form;
    }

    const pageById = new Map((translations.pages ?? []).map((row) => [row.page_id, row]));
    const inputById = new Map((translations.inputs ?? []).map((row) => [row.input_id, row]));
    const formTranslation = translations.form;

    const localizedPages = (form.pages ?? []).map((page) => {
        const pageTranslation = page.page_id ? pageById.get(page.page_id) : undefined;
        const localizedInputs = (page.inputs ?? []).map((input) => {
            const inputTranslation = input.input_id ? inputById.get(input.input_id) : undefined;
            if (!inputTranslation) {
                return input;
            }

            return {
                ...input,
                ...(inputTranslation.input_label != null ? { input_label: inputTranslation.input_label } : {}),
                ...(inputTranslation.input_caption != null ? { input_caption: inputTranslation.input_caption } : {}),
                ...(inputTranslation.input_description != null
                    ? { input_description: inputTranslation.input_description }
                    : {}),
                ...(inputTranslation.scale_options != null ? { scale_options: inputTranslation.scale_options } : {}),
                ...(inputTranslation.options != null ? { options: inputTranslation.options } : {}),
            };
        });

        return {
            ...page,
            ...(pageTranslation?.page_label != null ? { page_label: pageTranslation.page_label } : {}),
            ...(pageTranslation?.page_description != null
                ? { page_description: pageTranslation.page_description }
                : {}),
            inputs: localizedInputs,
        };
    });

    return {
        ...form,
        ...(formTranslation?.form_name != null ? { form_name: formTranslation.form_name } : {}),
        ...(formTranslation?.form_description != null ? { form_description: formTranslation.form_description } : {}),
        ...(formTranslation?.form_instructions != null ? { form_instructions: formTranslation.form_instructions } : {}),
        ...(formTranslation?.form_caption != null ? { form_caption: formTranslation.form_caption } : {}),
        pages: localizedPages,
        locale,
    };
}
